import React, { useState } from 'react';
import { 
  Drawer,
  Box,
  Typography,
  Divider,
  IconButton,
  Button,
  TextField,
  Grid,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChatIcon from '@mui/icons-material/Chat';

const JobDetailPanel = ({ job, isOpen, onClose, onMarkValid, onAddRemark }) => {
  const [remark, setRemark] = useState('');
  const [showRemarkForm, setShowRemarkForm] = useState(false);

  if (!job) return null;

  const handleAddRemark = () => {
    if (remark.trim()) {
      onAddRemark(job.id, remark);
      setRemark('');
      setShowRemarkForm(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 450 }, p: 3 }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Job Details</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          {job.title}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          {job.company}
        </Typography>
        <Typography variant="body2" gutterBottom>
          {job.location.city}, {job.location.state}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Posted on {job.date}
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Skills:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {job.skills.map((skill, index) => (
            <Chip key={index} label={skill} size="small" />
          ))}
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Source:
        </Typography>
        <Typography 
          variant="body2" 
          component="a" 
          href={job.source}
          target="_blank"
          rel="noopener noreferrer"
          color="primary"
          sx={{ wordBreak: 'break-all' }}
        >
          {job.source}
        </Typography>
      </Box>      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" gutterBottom>
          Description:
        </Typography>
        <Box 
          sx={{ 
            fontSize: 'body2.fontSize',
            fontFamily: 'body2.fontFamily',
            color: 'text.primary',
            '& a': { color: 'primary.main' },
            '& p': { mt: 1, mb: 1 }
          }} 
          dangerouslySetInnerHTML={{ __html: job.description }} 
        />
      </Box>

      {job.remark && (
        <Card variant="outlined" sx={{ mb: 4, bgcolor: '#FFFBEB' }}>
          <CardContent>
            <Typography variant="subtitle2" color="warning.main" gutterBottom>
              Remark:
            </Typography>
            <Typography variant="body2">
              {job.remark}
            </Typography>
          </CardContent>
        </Card>
      )}

      {showRemarkForm ? (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Add a Remark:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="Enter your observations about this job"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button 
                fullWidth 
                variant="outlined"
                onClick={() => setShowRemarkForm(false)}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button 
                fullWidth 
                variant="contained"
                color="primary"
                onClick={handleAddRemark}
              >
                Submit Remark
              </Button>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Divider sx={{ mb: 3 }} />
      )}      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircleOutlineIcon />}
          onClick={() => onMarkValid(job.id)}
          fullWidth
        >
          Mark as Valid
        </Button>
        
        {!showRemarkForm && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ChatIcon />}
            onClick={() => setShowRemarkForm(true)}
            fullWidth
          >
            Add Remark
          </Button>
        )}
      </Box>    </Drawer>
  );
};

JobDetailPanel.propTypes = {
  job: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    company: PropTypes.string,
    location: PropTypes.shape({
      city: PropTypes.string,
      state: PropTypes.string
    }),
    skills: PropTypes.arrayOf(PropTypes.string),
    date: PropTypes.string,
    description: PropTypes.string,
    source: PropTypes.string,
    remark: PropTypes.string,
    status: PropTypes.string
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onMarkValid: PropTypes.func.isRequired,
  onAddRemark: PropTypes.func.isRequired
};

export default JobDetailPanel;
